import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Box, Container, Typography, Paper, Grid, Button, Chip,
  CircularProgress, Tabs, Tab, Table, TableBody, TableCell,
  TableHead, TableRow, IconButton, Tooltip, Dialog, DialogTitle,
  DialogContent, DialogActions, TextField, FormControl, InputLabel,
  Select, MenuItem, Divider, ToggleButton, ToggleButtonGroup,
  LinearProgress, Autocomplete,
} from '@mui/material';
import {
  ArrowBack, Add, Delete, Edit, Refresh, MonitorHeart,
  Article, Medication, Dashboard, WarningAmber, CheckCircle,
  Person, CalendarToday, LocalHospital, PriorityHigh,
  Assignment, Receipt, Science, Water, MedicalServices, OpenInNew,
  AccountBalance, FolderOpen, SwapHoriz,
} from '@mui/icons-material';
import {
  ipdService, ipdVitalsService, ipdClinicalNoteService, ipdMedicationService,
  ipdInvestigationService, ipdFluidBalanceService, surgeryService,
  insuranceCaseService, insurerService, tpaService, wardService,
} from '../../services/api';
import SurgeryFormDialog from '../../components/SurgeryFormDialog';

const INSURANCE_STATUS_LABELS = {
  PreAuthPending: 'Pre-Auth Pending',
  PreAuthApproved: 'Pre-Auth Approved',
  DocumentationPending: 'Documentation Pending',
  SubmittedToTPA: 'Submitted to TPA',
  QueryRaised: 'Query Raised',
  Approved: 'Approved',
  Settled: 'Settled',
  Rejected: 'Rejected',
  Cancelled: 'Cancelled',
};

const INSURANCE_STATUS_COLORS = {
  PreAuthPending:       { bg: '#fff3e0', color: '#e65100' },
  PreAuthApproved:      { bg: '#e3f2fd', color: '#1565c0' },
  DocumentationPending: { bg: '#fff8e1', color: '#f57f17' },
  SubmittedToTPA:       { bg: '#e3f2fd', color: '#0277bd' },
  QueryRaised:          { bg: '#fce4ec', color: '#ad1457' },
  Approved:             { bg: '#e8f5e9', color: '#2e7d32' },
  Settled:              { bg: '#e8f5e9', color: '#1b5e20' },
  Rejected:             { bg: '#ffebee', color: '#c62828' },
  Cancelled:            { bg: '#eeeeee', color: '#616161' },
};

const SURGERY_STATUS_COLORS = {
  Scheduled:  { bg: '#e3f2fd', color: '#1565c0' },
  InProgress: { bg: '#fff3e0', color: '#e65100' },
  Completed:  { bg: '#e8f5e9', color: '#2e7d32' },
  Cancelled:  { bg: '#ffebee', color: '#c62828' },
  Postponed:  { bg: '#f3e5f5', color: '#6a1b9a' },
};

// ── Helpers ───────────────────────────────────────────────────────────────────
const calcAge = (dob) =>
  dob ? Math.floor((new Date() - new Date(dob)) / (1000 * 60 * 60 * 24 * 365.25)) : null;

const toLocalDT = (d) => {
  if (!d) return '';
  const dt = new Date(d);
  const p = n => String(n).padStart(2, '0');
  return `${dt.getFullYear()}-${p(dt.getMonth()+1)}-${p(dt.getDate())}T${p(dt.getHours())}:${p(dt.getMinutes())}`;
};

const fmtDate = (d) =>
  d ? new Date(d).toLocaleDateString('en-IN', { day:'2-digit', month:'short', year:'numeric' }) : '—';
const fmtDT  = (d) =>
  d ? new Date(d).toLocaleString('en-IN',   { day:'2-digit', month:'short', hour:'2-digit', minute:'2-digit' }) : '—';

// Normal ranges for color coding
const isLow = (type, v) => ({
  temperature: 36.1, bpSystolic: 90, bpDiastolic: 60,
  pulse: 60, respiratoryRate: 12, spO2: 95, bloodSugar: 70,
}[type] != null && v < { temperature:36.1,bpSystolic:90,bpDiastolic:60,pulse:60,respiratoryRate:12,spO2:95,bloodSugar:70 }[type]);

const isHigh = (type, v) => ({
  temperature:37.2,bpSystolic:140,bpDiastolic:90,pulse:100,respiratoryRate:20,bloodSugar:140
}[type] != null && v > {temperature:37.2,bpSystolic:140,bpDiastolic:90,pulse:100,respiratoryRate:20,bloodSugar:140}[type]);

const vitalColor = (type, v) => {
  if (v == null) return 'text.primary';
  if (isLow(type, v) || isHigh(type, v)) return '#c62828';
  return '#2e7d32';
};

const NOTE_TYPE_COLORS = {
  Doctor: { bg: '#e3f2fd', color: '#1565c0', label: 'Doctor' },
  Nurse: { bg: '#e8f5e9', color: '#2e7d32', label: 'Nurse' },
  Consultant: { bg: '#f3e5f5', color: '#6a1b9a', label: 'Consultant' },
};

const ROUTES = ['Oral','IV','IM','SC','SL','Topical','Inhaled','Rectal','Nasogastric'];
const FREQUENCIES = ['STAT','OD','BD','TDS','QID','SOS','Q4H','Q6H','Q8H','Q12H','Continuous'];
const ADM_STATUSES = ['Given','Held','Refused','Not Available'];

function TabPanel({ children, value, index }) {
  return value === index ? <Box sx={{ pt: 2 }}>{children}</Box> : null;
}

// ── Component ─────────────────────────────────────────────────────────────────
const IpdPatientRecord = () => {
  const { admissionId } = useParams();
  const navigate = useNavigate();
  const currentUser = JSON.parse(localStorage.getItem('user') || '{}');
  const canEdit = ['Admin','Doctor','Receptionist'].includes(currentUser.role);
  const isDoctor = ['Admin','Doctor'].includes(currentUser.role);

  const [tab, setTab] = useState(0);
  const [admission, setAdmission] = useState(null);
  const [loading, setLoading] = useState(true);

  // Phase 2 data
  const [vitals, setVitals] = useState([]);
  const [notes, setNotes] = useState([]);
  const [medications, setMedications] = useState([]);

  // Phase 3 data
  const [investigations, setInvestigations] = useState([]);
  const [fluidData, setFluidData] = useState({ entries: [], totalIntake: 0, totalOutput: 0, netBalance: 0, intake24h: 0, output24h: 0, balance24h: 0 });

  // Surgery
  const [surgeries, setSurgeries] = useState([]);
  const [surgeryDialog, setSurgeryDialog] = useState({ open: false, editing: null });

  // Insurance
  const [insuranceCase, setInsuranceCase] = useState(null);
  const [insuranceLoading, setInsuranceLoading] = useState(true);
  const [insuranceCreateDialog, setInsuranceCreateDialog] = useState(false);
  const [insuranceCreateForm, setInsuranceCreateForm] = useState({ claimType: 'Cashless', insurerId: null, tpaId: null, policyNumber: '', memberId: '' });
  const [insuranceInsurers, setInsuranceInsurers] = useState([]);
  const [insuranceTpas, setInsuranceTpas] = useState([]);
  const [savingInsurance, setSavingInsurance] = useState(false);

  // Investigation dialogs
  const [invDialog, setInvDialog] = useState({ open: false });
  const [invForm, setInvForm] = useState({ investigationName: '', category: 'Lab', orderedAt: toLocalDT(new Date()), notes: '' });
  const [savingInv, setSavingInv] = useState(false);
  const [resultDialog, setResultDialog] = useState({ open: false, id: null, name: '' });
  const [resultForm, setResultForm] = useState({ resultValue: '', unit: '', referenceRange: '', flag: 'Normal', resultAt: toLocalDT(new Date()), notes: '' });
  const [savingResult, setSavingResult] = useState(false);

  // Fluid balance dialog
  const [fluidDialog, setFluidDialog] = useState(false);
  const [fluidForm, setFluidForm] = useState({ entryType: 'Intake', category: 'IV Fluid', amountMl: '', description: '', recordedAt: toLocalDT(new Date()) });
  const [savingFluid, setSavingFluid] = useState(false);

  // Discharge dialog
  const [dischargeDialog, setDischargeDialog] = useState(false);
  const [dischargeForm, setDischargeForm] = useState({ status: 'Discharged', dischargeType: 'Improved', dischargeNotes: '', actualDischargeDate: toLocalDT(new Date()) });
  const [discharging, setDischarging] = useState(false);

  // Bed transfer dialog
  const [transferDialog, setTransferDialog] = useState(false);
  const [transferWards, setTransferWards] = useState([]);
  const [transferBeds, setTransferBeds] = useState([]);
  const [transferForm, setTransferForm] = useState({ wardId: '', bedId: '', reason: '' });
  const [transferring, setTransferring] = useState(false);
  const [transferHistory, setTransferHistory] = useState([]);

  // Vitals dialog
  const [vitalsDialog, setVitalsDialog] = useState(false);
  const [vitalsForm, setVitalsForm] = useState({ recordedAt: toLocalDT(new Date()), temperature: '', bpSystolic: '', bpDiastolic: '', pulse: '', respiratoryRate: '', spO2: '', weight: '', bloodSugar: '', notes: '' });
  const [savingVitals, setSavingVitals] = useState(false);

  // Clinical note dialog
  const [noteDialog, setNoteDialog] = useState({ open: false, editing: null });
  const [noteMode, setNoteMode] = useState('soap'); // 'soap' | 'free'
  const [noteForm, setNoteForm] = useState({ noteType: 'Doctor', title: '', subjective: '', objective: '', assessment: '', plan: '', freeText: '' });
  const [savingNote, setSavingNote] = useState(false);

  // Medication order dialog
  const [medDialog, setMedDialog] = useState(false);
  const [medForm, setMedForm] = useState({ medicineName: '', dose: '', route: 'Oral', frequency: 'OD', startDate: toLocalDT(new Date()), endDate: '', instructions: '' });
  const [savingMed, setSavingMed] = useState(false);

  // MAR administration dialog
  const [marDialog, setMarDialog] = useState({ open: false, orderId: null, medicineName: '' });
  const [marForm, setMarForm] = useState({ scheduledFor: toLocalDT(new Date()), administeredAt: toLocalDT(new Date()), status: 'Given', notes: '' });
  const [savingMar, setSavingMar] = useState(false);

  const loadAdmission = useCallback(async () => {
    try { const r = await ipdService.getById(admissionId); setAdmission(r.data); }
    catch { alert('Failed to load admission'); }
    finally { setLoading(false); }
  }, [admissionId]);

  const loadVitals = useCallback(async () => {
    try { const r = await ipdVitalsService.getAll(admissionId); setVitals(r.data); } catch {}
  }, [admissionId]);

  const loadNotes = useCallback(async () => {
    try { const r = await ipdClinicalNoteService.getAll(admissionId); setNotes(r.data); } catch {}
  }, [admissionId]);

  const loadMedications = useCallback(async () => {
    try { const r = await ipdMedicationService.getAll(admissionId); setMedications(r.data); } catch {}
  }, [admissionId]);

  const loadInvestigations = useCallback(async () => {
    try { const r = await ipdInvestigationService.getAll(admissionId); setInvestigations(r.data); } catch {}
  }, [admissionId]);

  const loadFluid = useCallback(async () => {
    try { const r = await ipdFluidBalanceService.getAll(admissionId); setFluidData(r.data); } catch {}
  }, [admissionId]);

  const loadSurgeries = useCallback(async () => {
    const patientId = admission?.patient?.id;
    if (!patientId) return;
    try { const r = await surgeryService.getByPatient(patientId); setSurgeries(r.data); } catch {}
  }, [admission?.patient?.id]);

  const loadInsuranceCase = useCallback(async () => {
    setInsuranceLoading(true);
    try {
      const r = await insuranceCaseService.getByAdmission(admissionId);
      setInsuranceCase(r.data);
    } catch {
      setInsuranceCase(null);
    } finally {
      setInsuranceLoading(false);
    }
  }, [admissionId]);

  useEffect(() => { loadAdmission(); }, [loadAdmission]);
  useEffect(() => { loadVitals(); }, [loadVitals]);
  useEffect(() => { loadNotes(); }, [loadNotes]);
  useEffect(() => { loadMedications(); }, [loadMedications]);
  useEffect(() => { loadInvestigations(); }, [loadInvestigations]);
  useEffect(() => { loadFluid(); }, [loadFluid]);
  useEffect(() => { loadSurgeries(); }, [loadSurgeries]);
  useEffect(() => { loadInsuranceCase(); }, [loadInsuranceCase]);

  // ── Discharge ──
  const handleDischarge = async (e) => {
    e.preventDefault();
    setDischarging(true);
    try {
      await ipdService.discharge(admissionId, { ...dischargeForm, actualDischargeDate: dischargeForm.actualDischargeDate || toLocalDT(new Date()) });
      setDischargeDialog(false);
      loadAdmission();
    } catch (err) { alert(err.response?.data?.message || 'Discharge failed'); }
    finally { setDischarging(false); }
  };

  // ── Bed Transfer ──
  const openTransferDialog = async () => {
    setTransferForm({ wardId: admission?.ward?.id || '', bedId: '', reason: '' });
    setTransferBeds([]);
    setTransferDialog(true);
    try { const r = await wardService.getAll(); setTransferWards((r.data || []).filter(w => w.isActive)); } catch { setTransferWards([]); }
    loadTransferHistory();
    if (admission?.ward?.id) loadTransferBeds(admission.ward.id);
  };

  const loadTransferBeds = async (wardId) => {
    try {
      const r = await wardService.getBeds(wardId);
      const usable = (r.data?.beds || []).filter(b =>
        b.id !== admission.bed?.id && (b.status === 'Available' || b.status === 'Reserved'));
      setTransferBeds(usable);
    } catch { setTransferBeds([]); }
  };

  const loadTransferHistory = async () => {
    try { const r = await ipdService.getBedTransfers(admissionId); setTransferHistory(r.data); } catch { setTransferHistory([]); }
  };

  const handleWardChangeForTransfer = (wardId) => {
    setTransferForm(p => ({ ...p, wardId, bedId: '' }));
    if (wardId) loadTransferBeds(wardId);
  };

  const handleTransferBed = async (e) => {
    e.preventDefault();
    if (!transferForm.bedId) return;
    setTransferring(true);
    try {
      await ipdService.transferBed(admissionId, { toBedId: transferForm.bedId, reason: transferForm.reason || null });
      setTransferDialog(false);
      loadAdmission();
    } catch (err) { alert(err.response?.data?.message || 'Bed transfer failed'); }
    finally { setTransferring(false); }
  };

  // ── Vitals ──
  const handleSaveVitals = async (e) => {
    e.preventDefault();
    setSavingVitals(true);
    const payload = {};
    if (vitalsForm.recordedAt) payload.recordedAt = vitalsForm.recordedAt;
    ['temperature','bpSystolic','bpDiastolic','pulse','respiratoryRate','spO2','weight','bloodSugar'].forEach(k => {
      if (vitalsForm[k] !== '') payload[k] = parseFloat(vitalsForm[k]);
    });
    payload.notes = vitalsForm.notes || null;
    try {
      await ipdVitalsService.create(admissionId, payload);
      setVitalsDialog(false);
      setVitalsForm({ recordedAt: toLocalDT(new Date()), temperature:'', bpSystolic:'', bpDiastolic:'', pulse:'', respiratoryRate:'', spO2:'', weight:'', bloodSugar:'', notes:'' });
      loadVitals(); loadAdmission();
    } catch (err) { alert(err.response?.data?.message || 'Failed to save vitals'); }
    finally { setSavingVitals(false); }
  };

  const handleDeleteVitals = async (id) => {
    if (!window.confirm('Delete this vitals record?')) return;
    try { await ipdVitalsService.delete(admissionId, id); loadVitals(); loadAdmission(); }
    catch (err) { alert(err.response?.data?.message || 'Failed to delete'); }
  };

  // ── Clinical Notes ──
  const openNoteDialog = (editing = null) => {
    setNoteDialog({ open: true, editing });
    if (editing) {
      setNoteMode(editing.freeText ? 'free' : 'soap');
      setNoteForm({ noteType: editing.noteType, title: editing.title || '', subjective: editing.subjective || '', objective: editing.objective || '', assessment: editing.assessment || '', plan: editing.plan || '', freeText: editing.freeText || '' });
    } else {
      setNoteMode('soap');
      setNoteForm({ noteType: 'Doctor', title: '', subjective: '', objective: '', assessment: '', plan: '', freeText: '' });
    }
  };

  const handleSaveNote = async (e) => {
    e.preventDefault();
    setSavingNote(true);
    const payload = { ...noteForm };
    if (noteMode === 'soap') payload.freeText = null;
    else { payload.subjective = null; payload.objective = null; payload.assessment = null; payload.plan = null; }
    try {
      if (noteDialog.editing)
        await ipdClinicalNoteService.update(admissionId, noteDialog.editing.id, payload);
      else
        await ipdClinicalNoteService.create(admissionId, payload);
      setNoteDialog({ open: false, editing: null });
      loadNotes(); loadAdmission();
    } catch (err) { alert(err.response?.data?.message || 'Failed to save note'); }
    finally { setSavingNote(false); }
  };

  const handleDeleteNote = async (id) => {
    if (!window.confirm('Delete this note?')) return;
    try { await ipdClinicalNoteService.delete(admissionId, id); loadNotes(); loadAdmission(); }
    catch (err) { alert(err.response?.data?.message || 'Failed to delete'); }
  };

  // ── Medications ──
  const handleSaveMed = async (e) => {
    e.preventDefault();
    if (!medForm.medicineName.trim()) { alert('Medicine name is required.'); return; }
    setSavingMed(true);
    try {
      await ipdMedicationService.create(admissionId, { ...medForm, startDate: medForm.startDate || toLocalDT(new Date()), endDate: medForm.endDate || null });
      setMedDialog(false);
      setMedForm({ medicineName:'', dose:'', route:'Oral', frequency:'OD', startDate: toLocalDT(new Date()), endDate:'', instructions:'' });
      loadMedications(); loadAdmission();
    } catch (err) { alert(err.response?.data?.message || 'Failed to save medication'); }
    finally { setSavingMed(false); }
  };

  const handleDiscontinueMed = async (id) => {
    if (!window.confirm('Discontinue this medication?')) return;
    try { await ipdMedicationService.discontinue(admissionId, id); loadMedications(); loadAdmission(); }
    catch (err) { alert(err.response?.data?.message || 'Failed to discontinue'); }
  };

  const handleDeleteMed = async (id) => {
    if (!window.confirm('Delete this medication order?')) return;
    try { await ipdMedicationService.delete(admissionId, id); loadMedications(); loadAdmission(); }
    catch (err) { alert(err.response?.data?.message || 'Failed to delete'); }
  };

  const openMarDialog = (order) => {
    setMarDialog({ open: true, orderId: order.id, medicineName: order.medicineName });
    setMarForm({ scheduledFor: toLocalDT(new Date()), administeredAt: toLocalDT(new Date()), status: 'Given', notes: '' });
  };

  const handleSaveMar = async (e) => {
    e.preventDefault();
    setSavingMar(true);
    try {
      await ipdMedicationService.administer(admissionId, marDialog.orderId, { ...marForm, scheduledFor: marForm.scheduledFor || toLocalDT(new Date()), administeredAt: marForm.administeredAt || null });
      setMarDialog({ open: false, orderId: null, medicineName: '' });
      loadMedications();
    } catch (err) { alert(err.response?.data?.message || 'Failed to record administration'); }
    finally { setSavingMar(false); }
  };

  // ── Insurance ──
  const openInsuranceCreateDialog = async () => {
    setInsuranceCreateForm({ claimType: 'Cashless', insurerId: null, tpaId: null, policyNumber: '', memberId: '' });
    setInsuranceCreateDialog(true);
    try {
      const [ir, tr] = await Promise.all([insurerService.getAll(), tpaService.getAll()]);
      setInsuranceInsurers(ir.data.filter(i => i.isActive));
      setInsuranceTpas(tr.data.filter(t => t.isActive));
    } catch {}
  };

  const handleCreateInsuranceCase = async (e) => {
    e.preventDefault();
    setSavingInsurance(true);
    try {
      await insuranceCaseService.create({
        admissionId: parseInt(admissionId),
        claimType: insuranceCreateForm.claimType,
        insurerId: insuranceCreateForm.insurerId?.id || null,
        tpaId: insuranceCreateForm.tpaId?.id || null,
        policyNumber: insuranceCreateForm.policyNumber,
        memberId: insuranceCreateForm.memberId,
      });
      setInsuranceCreateDialog(false);
      loadInsuranceCase();
    } catch (err) {
      alert(err.response?.data?.message || 'Failed to create insurance case');
    } finally {
      setSavingInsurance(false);
    }
  };

  if (loading) return (
    <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '60vh' }}>
      <CircularProgress />
    </Box>
  );
  if (!admission) return <Typography sx={{ p: 4 }}>Admission not found.</Typography>;

  const patient = admission.patient;
  const age = calcAge(patient?.dateOfBirth);
  const isActive = admission.status === 'Active';
  const lastV = admission.lastVitals;

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100">

      {/* ── Patient Header ── */}
      <Box sx={{ bgcolor: 'white', boxShadow: 2, px: 3, py: 2 }}>
        <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', flexWrap: 'wrap', gap: 1 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
            <IconButton size="small" onClick={() => navigate(-1)}><ArrowBack /></IconButton>
            <Box>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <Typography variant="h5" fontWeight={700}>
                  {patient ? `${patient.firstName} ${patient.lastName}` : '—'}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  {patient?.gender}{age ? `, ${age} yrs` : ''}
                </Typography>
                {admission.isMlc && <Chip label="MLC" color="error" size="small" icon={<PriorityHigh />} />}
                <Chip
                  size="small" label={admission.status}
                  sx={{ bgcolor: isActive ? '#e3f2fd' : '#f5f5f5', color: isActive ? '#1565c0' : '#757575', fontWeight: 700 }}
                />
              </Box>
              <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', mt: 0.25 }}>
                <Typography variant="caption" color="text.secondary">
                  <strong>Bed:</strong> {admission.bed?.bedNumber} · {admission.ward?.name}
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  <strong>Admitted:</strong> {fmtDate(admission.admissionDate)} · Day {admission.daysInHospital}
                </Typography>
                {admission.admittingDoctor && (
                  <Typography variant="caption" color="text.secondary">
                    <strong>Dr.</strong> {admission.admittingDoctor.firstName} {admission.admittingDoctor.lastName}
                    {admission.admittingDoctor.specialization ? ` (${admission.admittingDoctor.specialization})` : ''}
                  </Typography>
                )}
                {admission.expectedDischargeDate && (
                  <Typography variant="caption" color="text.secondary">
                    <strong>Expected discharge:</strong> {fmtDate(admission.expectedDischargeDate)}
                  </Typography>
                )}
              </Box>
            </Box>
          </Box>
          <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
            <Tooltip title="Refresh data">
              <IconButton size="small" onClick={() => { loadAdmission(); loadVitals(); loadNotes(); loadMedications(); loadInvestigations(); loadFluid(); loadSurgeries(); }}>
                <Refresh />
              </IconButton>
            </Tooltip>
            <Button
              variant="outlined" size="small"
              startIcon={<Assignment />}
              onClick={() => navigate(`/ipd/patient/${admissionId}/discharge-summary`)}
            >
              Discharge Summary
            </Button>
            <Button
              variant="outlined" size="small"
              startIcon={<Receipt />}
              onClick={() => navigate(`/ipd/patient/${admissionId}/billing`)}
            >
              Billing
            </Button>
            {canEdit && isActive && (
              <Button variant="outlined" size="small" startIcon={<SwapHoriz />} onClick={openTransferDialog}>
                Transfer Bed
              </Button>
            )}
            {canEdit && isActive && (
              <Button variant="contained" color="success" size="small" onClick={() => setDischargeDialog(true)}>
                Process Discharge
              </Button>
            )}
          </Box>
        </Box>
      </Box>

      <Container maxWidth="xl" sx={{ py: 3 }}>
        <Paper elevation={3} sx={{ borderRadius: 2 }}>
          {/* Tabs */}
          <Tabs value={tab} onChange={(_, v) => setTab(v)} sx={{ borderBottom: '1px solid #eee', px: 2 }} variant="scrollable" scrollButtons="auto">
            <Tab icon={<Dashboard />} iconPosition="start" label="Overview" />
            <Tab icon={<MonitorHeart />} iconPosition="start" label={`Vitals${admission.vitalsCount > 0 ? ` (${admission.vitalsCount})` : ''}`} />
            <Tab icon={<Article />} iconPosition="start" label={`Notes${admission.notesCount > 0 ? ` (${admission.notesCount})` : ''}`} />
            <Tab icon={<Medication />} iconPosition="start" label={`Medications${admission.activeMeds > 0 ? ` (${admission.activeMeds} active)` : ''}`} />
            <Tab icon={<Science />} iconPosition="start" label={`Investigations${investigations.length > 0 ? ` (${investigations.length})` : ''}`} />
            <Tab icon={<Water />} iconPosition="start" label="Fluid Balance" />
            <Tab icon={<MedicalServices />} iconPosition="start" label={`Surgery${surgeries.length > 0 ? ` (${surgeries.length})` : ''}`} />
            <Tab icon={<AccountBalance />} iconPosition="start" label="Insurance" />
          </Tabs>

          <Box sx={{ p: 3 }}>
            {/* ── Tab 0: Overview ── */}
            <TabPanel value={tab} index={0}>
              <Grid container spacing={2}>
                {/* Patient info */}
                <Grid item xs={12} md={6}>
                  <Paper variant="outlined" sx={{ p: 2, borderRadius: 2, height: '100%' }}>
                    <Typography variant="subtitle2" fontWeight={700} sx={{ mb: 1.5, display: 'flex', alignItems: 'center', gap: 0.75 }}>
                      <Person fontSize="small" /> Patient Information
                    </Typography>
                    {[
                      ['Name', patient ? `${patient.firstName} ${patient.lastName}` : '—'],
                      ['Age / Gender', `${age ?? '—'} yrs / ${patient?.gender ?? '—'}`],
                      ['Phone', patient?.phoneNumber ?? '—'],
                      ['Address', patient?.address ?? '—'],
                    ].map(([k, v]) => (
                      <Box key={k} sx={{ display: 'flex', gap: 1, mb: 0.5 }}>
                        <Typography variant="body2" color="text.secondary" sx={{ minWidth: 110 }}>{k}:</Typography>
                        <Typography variant="body2" fontWeight={500}>{v}</Typography>
                      </Box>
                    ))}
                  </Paper>
                </Grid>

                {/* Admission info */}
                <Grid item xs={12} md={6}>
                  <Paper variant="outlined" sx={{ p: 2, borderRadius: 2, height: '100%' }}>
                    <Typography variant="subtitle2" fontWeight={700} sx={{ mb: 1.5, display: 'flex', alignItems: 'center', gap: 0.75 }}>
                      <LocalHospital fontSize="small" /> Admission Details
                    </Typography>
                    {[
                      ['Type', admission.admissionType],
                      ['Diagnosis', admission.admissionDiagnosis ?? '—'],
                      ['ICD-10', admission.icd10Code ?? '—'],
                      ['Referred By', admission.referredBy ? `${admission.referredBy}${admission.referralFrom ? ` (${admission.referralFrom})` : ''}` : '—'],
                      ['MLC', admission.isMlc ? `Yes — ${admission.mlcDetails || 'Details pending'}` : 'No'],
                    ].map(([k, v]) => (
                      <Box key={k} sx={{ display: 'flex', gap: 1, mb: 0.5 }}>
                        <Typography variant="body2" color="text.secondary" sx={{ minWidth: 110 }}>{k}:</Typography>
                        <Typography variant="body2" fontWeight={500}>{v}</Typography>
                      </Box>
                    ))}
                  </Paper>
                </Grid>

                {/* Last vitals */}
                <Grid item xs={12}>
                  <Paper variant="outlined" sx={{ p: 2, borderRadius: 2 }}>
                    <Typography variant="subtitle2" fontWeight={700} sx={{ mb: 1.5 }}>
                      Last Recorded Vitals {lastV ? `(${fmtDT(lastV.recordedAt)})` : '— None recorded'}
                    </Typography>
                    {lastV ? (
                      <Grid container spacing={1.5}>
                        {[
                          { label: 'BP', value: lastV.bpSystolic ? `${lastV.bpSystolic}/${lastV.bpDiastolic}` : null, unit: 'mmHg', type: 'bpSystolic', raw: lastV.bpSystolic },
                          { label: 'Pulse', value: lastV.pulse, unit: 'bpm', type: 'pulse', raw: lastV.pulse },
                          { label: 'Temp', value: lastV.temperature, unit: '°C', type: 'temperature', raw: lastV.temperature },
                          { label: 'SpO₂', value: lastV.spO2, unit: '%', type: 'spO2', raw: lastV.spO2 },
                          { label: 'RR', value: lastV.respiratoryRate, unit: '/min', type: 'respiratoryRate', raw: lastV.respiratoryRate },
                        ].map(s => (
                          <Grid item xs={6} sm={2.4} key={s.label}>
                            <Box sx={{ textAlign: 'center', p: 1.5, border: '1px solid #e0e0e0', borderRadius: 2, bgcolor: s.raw && (isLow(s.type, s.raw) || isHigh(s.type, s.raw)) ? '#fff5f5' : '#f9f9f9' }}>
                              <Typography variant="h5" fontWeight={700} sx={{ color: s.raw ? vitalColor(s.type, s.raw) : 'text.disabled' }}>
                                {s.value ?? '—'}
                              </Typography>
                              <Typography variant="caption" color="text.secondary">{s.label} {s.unit}</Typography>
                            </Box>
                          </Grid>
                        ))}
                      </Grid>
                    ) : (
                      <Typography variant="body2" color="text.secondary">No vitals recorded yet. Go to the Vitals tab to add.</Typography>
                    )}
                  </Paper>
                </Grid>

                {/* Recent notes + active meds summary */}
                <Grid item xs={12} md={6}>
                  <Paper variant="outlined" sx={{ p: 2, borderRadius: 2 }}>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                      <Typography variant="subtitle2" fontWeight={700}>Recent Notes</Typography>
                      <Button size="small" onClick={() => setTab(2)}>View All</Button>
                    </Box>
                    {notes.slice(0, 3).map(n => {
                      const nc = NOTE_TYPE_COLORS[n.noteType] || NOTE_TYPE_COLORS.Doctor;
                      return (
                        <Box key={n.id} sx={{ mb: 1, p: 1, borderLeft: `3px solid ${nc.color}`, bgcolor: nc.bg, borderRadius: '0 4px 4px 0' }}>
                          <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                            <Typography variant="caption" fontWeight={700} sx={{ color: nc.color }}>{n.noteType}</Typography>
                            <Typography variant="caption" color="text.secondary">{fmtDT(n.createdAt)}</Typography>
                          </Box>
                          {n.title && <Typography variant="body2" fontWeight={600}>{n.title}</Typography>}
                          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', whiteSpace: 'normal', wordBreak: 'break-word' }}>
                            {n.freeText ? n.freeText.slice(0, 120) : n.subjective ? `S: ${n.subjective.slice(0, 80)}` : '—'}
                            {(n.freeText?.length > 120 || n.subjective?.length > 80) ? '…' : ''}
                          </Typography>
                        </Box>
                      );
                    })}
                    {notes.length === 0 && <Typography variant="body2" color="text.secondary">No notes yet.</Typography>}
                  </Paper>
                </Grid>

                <Grid item xs={12} md={6}>
                  <Paper variant="outlined" sx={{ p: 2, borderRadius: 2 }}>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                      <Typography variant="subtitle2" fontWeight={700}>Active Medications</Typography>
                      <Button size="small" onClick={() => setTab(3)}>View All</Button>
                    </Box>
                    {medications.filter(m => m.status === 'Active').slice(0, 5).map(m => (
                      <Box key={m.id} sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.75, pb: 0.75, borderBottom: '1px solid #f0f0f0' }}>
                        <Box>
                          <Typography variant="body2" fontWeight={600}>{m.medicineName}</Typography>
                          <Typography variant="caption" color="text.secondary">{m.dose} · {m.route} · {m.frequency}</Typography>
                        </Box>
                        <Chip label="Active" size="small" color="success" variant="outlined" sx={{ height: 20, fontSize: '0.65rem' }} />
                      </Box>
                    ))}
                    {medications.filter(m => m.status === 'Active').length === 0 && (
                      <Typography variant="body2" color="text.secondary">No active medications.</Typography>
                    )}
                  </Paper>
                </Grid>
              </Grid>
            </TabPanel>

            {/* ── Tab 1: Vitals ── */}
            <TabPanel value={tab} index={1}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 2 }}>
                <Typography variant="h6" fontWeight={700}>Vitals Record</Typography>
                {canEdit && isActive && (
                  <Button variant="contained" startIcon={<Add />} onClick={() => setVitalsDialog(true)}>Add Vitals</Button>
                )}
              </Box>

              {vitals.length === 0 ? (
                <Typography color="text.secondary" sx={{ textAlign: 'center', py: 6 }}>No vitals recorded. Click "Add Vitals" to record.</Typography>
              ) : (
                <Box sx={{ overflowX: 'auto' }}>
                  <Table size="small">
                    <TableHead>
                      <TableRow sx={{ bgcolor: '#fafafa' }}>
                        <TableCell><strong>Date / Time</strong></TableCell>
                        <TableCell align="center"><strong>Temp (°C)</strong></TableCell>
                        <TableCell align="center"><strong>BP (mmHg)</strong></TableCell>
                        <TableCell align="center"><strong>Pulse (bpm)</strong></TableCell>
                        <TableCell align="center"><strong>RR (/min)</strong></TableCell>
                        <TableCell align="center"><strong>SpO₂ (%)</strong></TableCell>
                        <TableCell align="center"><strong>Weight (kg)</strong></TableCell>
                        <TableCell align="center"><strong>Sugar (mg/dL)</strong></TableCell>
                        <TableCell><strong>By</strong></TableCell>
                        <TableCell><strong>Notes</strong></TableCell>
                        {isDoctor && <TableCell />}
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {vitals.map(v => (
                        <TableRow key={v.id} hover>
                          <TableCell sx={{ whiteSpace: 'nowrap' }}>{fmtDT(v.recordedAt)}</TableCell>
                          <TableCell align="center">
                            <Typography variant="body2" fontWeight={600} sx={{ color: v.temperature ? vitalColor('temperature', v.temperature) : 'text.secondary' }}>
                              {v.temperature ?? '—'}
                            </Typography>
                          </TableCell>
                          <TableCell align="center">
                            <Typography variant="body2" fontWeight={600} sx={{ color: v.bpSystolic ? vitalColor('bpSystolic', v.bpSystolic) : 'text.secondary' }}>
                              {v.bpSystolic ? `${v.bpSystolic}/${v.bpDiastolic}` : '—'}
                            </Typography>
                          </TableCell>
                          <TableCell align="center">
                            <Typography variant="body2" fontWeight={600} sx={{ color: v.pulse ? vitalColor('pulse', v.pulse) : 'text.secondary' }}>
                              {v.pulse ?? '—'}
                            </Typography>
                          </TableCell>
                          <TableCell align="center">
                            <Typography variant="body2" fontWeight={600} sx={{ color: v.respiratoryRate ? vitalColor('respiratoryRate', v.respiratoryRate) : 'text.secondary' }}>
                              {v.respiratoryRate ?? '—'}
                            </Typography>
                          </TableCell>
                          <TableCell align="center">
                            <Typography variant="body2" fontWeight={600} sx={{ color: v.spO2 ? vitalColor('spO2', v.spO2) : 'text.secondary' }}>
                              {v.spO2 ?? '—'}
                            </Typography>
                          </TableCell>
                          <TableCell align="center"><Typography variant="body2">{v.weight ?? '—'}</Typography></TableCell>
                          <TableCell align="center">
                            <Typography variant="body2" sx={{ color: v.bloodSugar ? vitalColor('bloodSugar', v.bloodSugar) : 'text.secondary' }}>
                              {v.bloodSugar ?? '—'}
                            </Typography>
                          </TableCell>
                          <TableCell>
                            <Typography variant="caption" color="text.secondary">
                              {v.recordedBy ? `${v.recordedBy.firstName} ${v.recordedBy.lastName}` : '—'}
                            </Typography>
                          </TableCell>
                          <TableCell><Typography variant="caption" color="text.secondary">{v.notes ?? ''}</Typography></TableCell>
                          {isDoctor && (
                            <TableCell>
                              <Tooltip title="Delete"><IconButton size="small" color="error" onClick={() => handleDeleteVitals(v.id)}><Delete fontSize="small" /></IconButton></Tooltip>
                            </TableCell>
                          )}
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </Box>
              )}

              {vitals.length > 0 && (
                <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
                  Values in <span style={{ color: '#c62828', fontWeight: 700 }}>red</span> are outside normal range.
                  Normal: Temp 36.1–37.2°C · BP 90–140/60–90 · Pulse 60–100 · RR 12–20 · SpO₂ ≥95%
                </Typography>
              )}
            </TabPanel>

            {/* ── Tab 2: Clinical Notes ── */}
            <TabPanel value={tab} index={2}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 2 }}>
                <Typography variant="h6" fontWeight={700}>Clinical Notes</Typography>
                {canEdit && isActive && (
                  <Button variant="contained" startIcon={<Add />} onClick={() => openNoteDialog()}>Add Note</Button>
                )}
              </Box>

              {notes.length === 0 ? (
                <Typography color="text.secondary" sx={{ textAlign: 'center', py: 6 }}>No clinical notes yet.</Typography>
              ) : (
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                  {notes.map(n => {
                    const nc = NOTE_TYPE_COLORS[n.noteType] || NOTE_TYPE_COLORS.Doctor;
                    return (
                      <Paper key={n.id} variant="outlined" sx={{ p: 2, borderLeft: `4px solid ${nc.color}` }}>
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 1 }}>
                          <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
                            <Chip size="small" label={n.noteType} sx={{ bgcolor: nc.bg, color: nc.color, fontWeight: 700, fontSize: '0.7rem' }} />
                            {n.title && <Typography variant="subtitle2" fontWeight={700}>{n.title}</Typography>}
                          </Box>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <Typography variant="caption" color="text.secondary">
                              {n.author ? `${n.author.firstName} ${n.author.lastName}` : 'Unknown'} · {fmtDT(n.createdAt)}
                            </Typography>
                            {canEdit && isActive && (
                              <>
                                <Tooltip title="Edit"><IconButton size="small" onClick={() => openNoteDialog(n)}><Edit fontSize="small" /></IconButton></Tooltip>
                                <Tooltip title="Delete"><IconButton size="small" color="error" onClick={() => handleDeleteNote(n.id)}><Delete fontSize="small" /></IconButton></Tooltip>
                              </>
                            )}
                          </Box>
                        </Box>
                        {n.freeText ? (
                          <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>{n.freeText}</Typography>
                        ) : (
                          <Grid container spacing={1}>
                            {[['S — Subjective', n.subjective],['O — Objective', n.objective],['A — Assessment', n.assessment],['P — Plan', n.plan]].map(([label, val]) => val ? (
                              <Grid item xs={12} sm={6} key={label}>
                                <Typography variant="caption" fontWeight={700} sx={{ color: nc.color }}>{label}</Typography>
                                <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap', mt: 0.25 }}>{val}</Typography>
                              </Grid>
                            ) : null)}
                          </Grid>
                        )}
                      </Paper>
                    );
                  })}
                </Box>
              )}
            </TabPanel>

            {/* ── Tab 3: Medications (MAR) ── */}
            <TabPanel value={tab} index={3}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 2 }}>
                <Typography variant="h6" fontWeight={700}>Medications & Administration</Typography>
                {isDoctor && isActive && (
                  <Button variant="contained" startIcon={<Add />} onClick={() => setMedDialog(true)}>Order Medication</Button>
                )}
              </Box>

              {medications.length === 0 ? (
                <Typography color="text.secondary" sx={{ textAlign: 'center', py: 6 }}>No medications ordered.</Typography>
              ) : (
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                  {medications.map(m => (
                    <Paper key={m.id} variant="outlined" sx={{ p: 2, opacity: m.status !== 'Active' ? 0.7 : 1 }}>
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 1, flexWrap: 'wrap', gap: 1 }}>
                        <Box>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <Typography variant="subtitle1" fontWeight={700}>{m.medicineName}</Typography>
                            <Chip size="small" label={m.status}
                              color={m.status === 'Active' ? 'success' : m.status === 'Discontinued' ? 'error' : 'default'}
                              variant="outlined" sx={{ height: 20, fontSize: '0.65rem' }}
                            />
                          </Box>
                          <Typography variant="body2" color="text.secondary">
                            {[m.dose, m.route, m.frequency].filter(Boolean).join(' · ')}
                            {m.instructions ? ` — ${m.instructions}` : ''}
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            Started: {fmtDate(m.startDate)}
                            {m.endDate ? ` · Ended: ${fmtDate(m.endDate)}` : ''}
                            {m.prescribedBy ? ` · Ordered by Dr. ${m.prescribedBy.firstName} ${m.prescribedBy.lastName}` : ''}
                          </Typography>
                        </Box>
                        {isActive && (
                          <Box sx={{ display: 'flex', gap: 0.5 }}>
                            {m.status === 'Active' && canEdit && (
                              <Button size="small" variant="outlined" color="success" onClick={() => openMarDialog(m)}>
                                Record Given
                              </Button>
                            )}
                            {m.status === 'Active' && isDoctor && (
                              <Button size="small" variant="outlined" color="error" onClick={() => handleDiscontinueMed(m.id)}>
                                Discontinue
                              </Button>
                            )}
                            {isDoctor && m.status === 'Active' && m.administrations?.length === 0 && (
                              <Tooltip title="Delete order">
                                <IconButton size="small" color="error" onClick={() => handleDeleteMed(m.id)}>
                                  <Delete fontSize="small" />
                                </IconButton>
                              </Tooltip>
                            )}
                          </Box>
                        )}
                      </Box>

                      {/* Administration history */}
                      {m.administrations && m.administrations.length > 0 && (
                        <>
                          <Divider sx={{ my: 1 }} />
                          <Typography variant="caption" fontWeight={700} color="text.secondary">Administration History</Typography>
                          <Box sx={{ mt: 0.75, display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                            {m.administrations.map(a => {
                              const statusColors = { Given:'#2e7d32', Held:'#f57f17', Refused:'#c62828', 'Not Available':'#757575', Pending:'#1565c0' };
                              return (
                                <Box key={a.id} sx={{ display: 'flex', gap: 1, alignItems: 'center', flexWrap: 'wrap' }}>
                                  <Chip size="small" label={a.status} sx={{ height: 18, fontSize: '0.65rem', bgcolor: (statusColors[a.status] || '#9e9e9e') + '20', color: statusColors[a.status] || '#9e9e9e', fontWeight: 700 }} />
                                  <Typography variant="caption" color="text.secondary">
                                    Scheduled: {fmtDT(a.scheduledFor)}
                                    {a.administeredAt ? ` · Given: ${fmtDT(a.administeredAt)}` : ''}
                                    {a.administeredBy ? ` · by ${a.administeredBy.firstName} ${a.administeredBy.lastName}` : ''}
                                    {a.notes ? ` — ${a.notes}` : ''}
                                  </Typography>
                                </Box>
                              );
                            })}
                          </Box>
                        </>
                      )}
                    </Paper>
                  ))}
                </Box>
              )}
            </TabPanel>

            {/* ── Tab 4: Investigations ── */}
            <TabPanel value={tab} index={4}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 2 }}>
                <Typography variant="h6" fontWeight={700}>Investigations</Typography>
                {canEdit && (
                  <Button variant="contained" startIcon={<Add />} onClick={() => {
                    setInvForm({ investigationName: '', category: 'Lab', orderedAt: toLocalDT(new Date()), notes: '' });
                    setInvDialog({ open: true });
                  }}>Order Investigation</Button>
                )}
              </Box>

              {investigations.length === 0 ? (
                <Typography color="text.secondary" sx={{ textAlign: 'center', py: 6 }}>No investigations ordered.</Typography>
              ) : (
                <Table size="small">
                  <TableHead>
                    <TableRow sx={{ bgcolor: '#f5f5f5' }}>
                      <TableCell><strong>Investigation</strong></TableCell>
                      <TableCell><strong>Category</strong></TableCell>
                      <TableCell><strong>Ordered</strong></TableCell>
                      <TableCell><strong>Result</strong></TableCell>
                      <TableCell><strong>Range</strong></TableCell>
                      <TableCell><strong>Status</strong></TableCell>
                      <TableCell><strong>Actions</strong></TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {investigations.map(inv => {
                      const flagColor = { High: '#e65100', Low: '#1565c0', Critical: '#b71c1c', Normal: '#2e7d32' }[inv.flag] || 'text.primary';
                      return (
                        <TableRow key={inv.id} hover sx={{ opacity: inv.status === 'Cancelled' ? 0.5 : 1 }}>
                          <TableCell>
                            <Typography variant="body2" fontWeight={600}>{inv.investigationName}</Typography>
                            {inv.orderedByName && <Typography variant="caption" color="text.secondary">Dr. {inv.orderedByName}</Typography>}
                          </TableCell>
                          <TableCell>
                            <Chip size="small" label={inv.category} variant="outlined"
                              sx={{ fontSize: '0.65rem', height: 20 }} />
                          </TableCell>
                          <TableCell>
                            <Typography variant="caption">{fmtDT(inv.orderedAt)}</Typography>
                          </TableCell>
                          <TableCell>
                            {inv.resultValue ? (
                              <Typography variant="body2" fontWeight={700} sx={{ color: flagColor }}>
                                {inv.resultValue} {inv.unit || ''}
                                {inv.flag && inv.flag !== 'Normal' && (
                                  <Chip size="small" label={inv.flag} sx={{ ml: 0.5, height: 16, fontSize: '0.6rem', bgcolor: flagColor + '20', color: flagColor }} />
                                )}
                              </Typography>
                            ) : <Typography variant="caption" color="text.secondary">Pending</Typography>}
                          </TableCell>
                          <TableCell>
                            <Typography variant="caption" color="text.secondary">{inv.referenceRange || '—'}</Typography>
                          </TableCell>
                          <TableCell>
                            <Chip size="small" label={inv.status}
                              color={inv.status === 'Resulted' ? 'success' : inv.status === 'Cancelled' ? 'error' : 'warning'}
                              variant="outlined" sx={{ fontSize: '0.65rem', height: 20 }} />
                          </TableCell>
                          <TableCell>
                            <Box sx={{ display: 'flex', gap: 0.5 }}>
                              {canEdit && inv.status === 'Ordered' && (
                                <Tooltip title="Enter Result">
                                  <Button size="small" variant="outlined" color="primary" sx={{ fontSize: '0.65rem', py: 0.25, px: 0.75, minWidth: 0 }}
                                    onClick={() => {
                                      setResultDialog({ open: true, id: inv.id, name: inv.investigationName });
                                      setResultForm({ resultValue: '', unit: inv.unit || '', referenceRange: inv.referenceRange || '', flag: 'Normal', resultAt: toLocalDT(new Date()), notes: '' });
                                    }}>
                                    Result
                                  </Button>
                                </Tooltip>
                              )}
                              {isDoctor && inv.status === 'Ordered' && (
                                <Tooltip title="Cancel">
                                  <IconButton size="small" color="warning" onClick={async () => {
                                    await ipdInvestigationService.cancel(admissionId, inv.id);
                                    loadInvestigations();
                                  }}>
                                    <Delete fontSize="small" />
                                  </IconButton>
                                </Tooltip>
                              )}
                              {isDoctor && (
                                <Tooltip title="Delete">
                                  <IconButton size="small" color="error" onClick={async () => {
                                    if (window.confirm('Delete this investigation record?')) {
                                      await ipdInvestigationService.delete(admissionId, inv.id);
                                      loadInvestigations();
                                    }
                                  }}>
                                    <Delete fontSize="small" />
                                  </IconButton>
                                </Tooltip>
                              )}
                            </Box>
                          </TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              )}
            </TabPanel>

            {/* ── Tab 5: Fluid Balance ── */}
            <TabPanel value={tab} index={5}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                <Typography variant="h6" fontWeight={700}>Fluid Balance</Typography>
                {canEdit && (
                  <Button variant="contained" startIcon={<Add />} onClick={() => {
                    setFluidForm({ entryType: 'Intake', category: 'IV Fluid', amountMl: '', description: '', recordedAt: toLocalDT(new Date()) });
                    setFluidDialog(true);
                  }}>Add Entry</Button>
                )}
              </Box>

              {/* Summary Cards */}
              <Grid container spacing={2} sx={{ mb: 3 }}>
                {[
                  { label: 'Total Intake', value: fluidData.totalIntake, color: '#1565c0', bg: '#e3f2fd' },
                  { label: 'Total Output', value: fluidData.totalOutput, color: '#c62828', bg: '#ffebee' },
                  { label: 'Net Balance', value: fluidData.netBalance, color: fluidData.netBalance >= 0 ? '#2e7d32' : '#e65100', bg: fluidData.netBalance >= 0 ? '#e8f5e9' : '#fff3e0' },
                  { label: '24h Balance', value: fluidData.balance24h, color: fluidData.balance24h >= 0 ? '#2e7d32' : '#e65100', bg: fluidData.balance24h >= 0 ? '#e8f5e9' : '#fff3e0' },
                ].map(({ label, value, color, bg }) => (
                  <Grid item xs={6} sm={3} key={label}>
                    <Paper variant="outlined" sx={{ p: 1.5, bgcolor: bg, borderColor: color + '40', textAlign: 'center' }}>
                      <Typography variant="caption" color="text.secondary" display="block">{label}</Typography>
                      <Typography variant="h6" fontWeight={700} sx={{ color }}>
                        {value >= 0 ? '+' : ''}{Number(value).toFixed(0)} mL
                      </Typography>
                    </Paper>
                  </Grid>
                ))}
              </Grid>

              {/* Entries Table */}
              {fluidData.entries.length === 0 ? (
                <Typography color="text.secondary" sx={{ textAlign: 'center', py: 6 }}>No fluid entries recorded.</Typography>
              ) : (
                <Table size="small">
                  <TableHead>
                    <TableRow sx={{ bgcolor: '#f5f5f5' }}>
                      <TableCell><strong>Type</strong></TableCell>
                      <TableCell><strong>Category</strong></TableCell>
                      <TableCell align="right"><strong>Amount (mL)</strong></TableCell>
                      <TableCell><strong>Description</strong></TableCell>
                      <TableCell><strong>Recorded At</strong></TableCell>
                      <TableCell><strong>By</strong></TableCell>
                      <TableCell />
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {[...fluidData.entries].sort((a, b) => new Date(b.recordedAt) - new Date(a.recordedAt)).map(entry => (
                      <TableRow key={entry.id} hover>
                        <TableCell>
                          <Chip size="small" label={entry.entryType}
                            sx={{ fontSize: '0.65rem', height: 20,
                              bgcolor: entry.entryType === 'Intake' ? '#e3f2fd' : '#ffebee',
                              color:   entry.entryType === 'Intake' ? '#1565c0' : '#c62828',
                              fontWeight: 700 }} />
                        </TableCell>
                        <TableCell>
                          <Typography variant="caption">{entry.category}</Typography>
                        </TableCell>
                        <TableCell align="right">
                          <Typography variant="body2" fontWeight={600}
                            sx={{ color: entry.entryType === 'Intake' ? '#1565c0' : '#c62828' }}>
                            {entry.entryType === 'Intake' ? '+' : '-'}{Number(entry.amountMl).toFixed(0)}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Typography variant="caption" color="text.secondary">{entry.description || '—'}</Typography>
                        </TableCell>
                        <TableCell>
                          <Typography variant="caption">{fmtDT(entry.recordedAt)}</Typography>
                        </TableCell>
                        <TableCell>
                          <Typography variant="caption" color="text.secondary">{entry.recordedByName || '—'}</Typography>
                        </TableCell>
                        <TableCell>
                          {canEdit && (
                            <Tooltip title="Delete entry">
                              <IconButton size="small" color="error" onClick={async () => {
                                if (window.confirm('Delete this fluid entry?')) {
                                  await ipdFluidBalanceService.delete(admissionId, entry.id);
                                  loadFluid();
                                }
                              }}>
                                <Delete fontSize="small" />
                              </IconButton>
                            </Tooltip>
                          )}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              )}
            </TabPanel>

            {/* ── Tab 6: Surgery ── */}
            <TabPanel value={tab} index={6}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 2 }}>
                <Typography variant="h6" fontWeight={700}>Surgeries</Typography>
                {isDoctor && (
                  <Button variant="contained" startIcon={<Add />} onClick={() => setSurgeryDialog({ open: true, editing: null })}>
                    Plan Surgery
                  </Button>
                )}
              </Box>

              {surgeries.length === 0 ? (
                <Typography color="text.secondary" sx={{ textAlign: 'center', py: 6 }}>No surgeries planned for this patient.</Typography>
              ) : (
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                  {surgeries.map(s => {
                    const sc = SURGERY_STATUS_COLORS[s.status] || SURGERY_STATUS_COLORS.Scheduled;
                    return (
                      <Paper key={s.id} variant="outlined" sx={{ p: 2 }}>
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 1, flexWrap: 'wrap', gap: 1 }}>
                          <Box>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                              <Typography variant="subtitle1" fontWeight={700}>{s.surgeryName}</Typography>
                              <Chip size="small" label={s.status} sx={{ bgcolor: sc.bg, color: sc.color, fontWeight: 700, fontSize: '0.65rem', height: 20 }} />
                            </Box>
                            <Typography variant="body2" color="text.secondary">
                              {fmtDT(s.scheduledDateTime)}
                              {s.surgeon ? ` · Dr. ${s.surgeon.firstName} ${s.surgeon.lastName}` : ''}
                              {s.otRoom ? ` · ${s.otRoom.name}` : ''}
                              {s.estimatedDurationMinutes ? ` · ${s.estimatedDurationMinutes} min` : ''}
                            </Typography>
                            {s.surgeryType && (
                              <Typography variant="caption" color="text.secondary">Type: {s.surgeryType.name}</Typography>
                            )}
                            {s.teamMembers?.length > 0 && (
                              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, mt: 0.5 }}>
                                {s.teamMembers.map((tm, i) => (
                                  <Chip key={i} size="small" variant="outlined" label={`${tm.role}: ${tm.user?.firstName} ${tm.user?.lastName}`} sx={{ height: 20, fontSize: '0.65rem' }} />
                                ))}
                              </Box>
                            )}
                            {s.notes && (
                              <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5 }}>{s.notes}</Typography>
                            )}
                          </Box>
                          <Box sx={{ display: 'flex', gap: 0.5 }}>
                            {isDoctor && (
                              <Tooltip title="Edit">
                                <IconButton size="small" onClick={() => setSurgeryDialog({ open: true, editing: s })}>
                                  <Edit fontSize="small" />
                                </IconButton>
                              </Tooltip>
                            )}
                            <Tooltip title="Open in Surgery Planner">
                              <IconButton size="small" color="primary" onClick={() => navigate(`/surgery-planner/${s.id}`)}>
                                <OpenInNew fontSize="small" />
                              </IconButton>
                            </Tooltip>
                          </Box>
                        </Box>
                      </Paper>
                    );
                  })}
                </Box>
              )}
            </TabPanel>

            {/* ── Tab 7: Insurance ── */}
            <TabPanel value={tab} index={7}>
              {insuranceLoading ? (
                <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
                  <CircularProgress />
                </Box>
              ) : !insuranceCase ? (
                <Box sx={{ textAlign: 'center', py: 4 }}>
                  <Typography color="text.secondary" sx={{ mb: 2 }}>
                    Self-Pay — no insurance case on file for this admission.
                  </Typography>
                  {canEdit && (
                    <Button variant="contained" startIcon={<Add />} onClick={openInsuranceCreateDialog}>
                      Create Insurance Case
                    </Button>
                  )}
                </Box>
              ) : (
                <Box>
                  <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2, flexWrap: 'wrap', gap: 1 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, flexWrap: 'wrap' }}>
                      <Typography variant="h6">{insuranceCase.claimType} Claim</Typography>
                      <Chip
                        size="small"
                        label={INSURANCE_STATUS_LABELS[insuranceCase.status] || insuranceCase.status}
                        sx={{
                          bgcolor: INSURANCE_STATUS_COLORS[insuranceCase.status]?.bg || '#eeeeee',
                          color: INSURANCE_STATUS_COLORS[insuranceCase.status]?.color || '#616161',
                          fontWeight: 700,
                        }}
                      />
                    </Box>
                    <Tooltip title="Open in Insurance Desk">
                      <IconButton size="small" color="primary" onClick={() => navigate('/insurance-desk')}>
                        <OpenInNew fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  </Box>

                  <Grid container spacing={2} sx={{ mb: 2 }}>
                    <Grid item xs={12} sm={6} md={3}>
                      <Typography variant="caption" color="text.secondary">Insurer</Typography>
                      <Typography variant="body2">{insuranceCase.insurer?.name || '—'}</Typography>
                    </Grid>
                    <Grid item xs={12} sm={6} md={3}>
                      <Typography variant="caption" color="text.secondary">TPA</Typography>
                      <Typography variant="body2">{insuranceCase.tpa?.name || '—'}</Typography>
                    </Grid>
                    <Grid item xs={12} sm={6} md={3}>
                      <Typography variant="caption" color="text.secondary">Policy Number</Typography>
                      <Typography variant="body2">{insuranceCase.policyNumber || '—'}</Typography>
                    </Grid>
                    <Grid item xs={12} sm={6} md={3}>
                      <Typography variant="caption" color="text.secondary">Member ID</Typography>
                      <Typography variant="body2">{insuranceCase.memberId || '—'}</Typography>
                    </Grid>
                    <Grid item xs={12} sm={6} md={3}>
                      <Typography variant="caption" color="text.secondary">Pre-Auth Amount</Typography>
                      <Typography variant="body2">{insuranceCase.preAuthAmount != null ? `₹${insuranceCase.preAuthAmount}` : '—'}</Typography>
                    </Grid>
                    <Grid item xs={12} sm={6} md={3}>
                      <Typography variant="caption" color="text.secondary">Approved Amount</Typography>
                      <Typography variant="body2">{insuranceCase.approvedAmount != null ? `₹${insuranceCase.approvedAmount}` : '—'}</Typography>
                    </Grid>
                    <Grid item xs={12} sm={6} md={3}>
                      <Typography variant="caption" color="text.secondary">Settled Amount</Typography>
                      <Typography variant="body2">{insuranceCase.settledAmount != null ? `₹${insuranceCase.settledAmount}` : '—'}</Typography>
                    </Grid>
                    <Grid item xs={12} sm={6} md={3}>
                      <Typography variant="caption" color="text.secondary">Documents Ready</Typography>
                      <Typography variant="body2">
                        {insuranceCase.documents?.filter(d => d.isCompleted).length || 0} / {insuranceCase.documents?.length || 0}
                      </Typography>
                    </Grid>
                  </Grid>

                  <Box sx={{ display: 'flex', gap: 1.5, flexWrap: 'wrap' }}>
                    <Button variant="outlined" startIcon={<OpenInNew />} onClick={() => navigate('/insurance-desk')}>
                      Manage in Insurance Desk
                    </Button>
                    <Button variant="contained" startIcon={<FolderOpen />} onClick={() => navigate(`/insurance-cases/${insuranceCase.id}/packet`)}>
                      Open Document Packet
                    </Button>
                  </Box>
                </Box>
              )}
            </TabPanel>
          </Box>
        </Paper>
      </Container>

      {/* ── Discharge Dialog ── */}
      <Dialog open={dischargeDialog} onClose={() => setDischargeDialog(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Process Discharge — {patient ? `${patient.firstName} ${patient.lastName}` : ''}</DialogTitle>
        <form onSubmit={handleDischarge}>
          <DialogContent sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 1 }}>
            <FormControl fullWidth required>
              <InputLabel>Discharge Status</InputLabel>
              <Select value={dischargeForm.status} label="Discharge Status *" onChange={e => setDischargeForm(p => ({ ...p, status: e.target.value }))}>
                {['Discharged','LAMA','Transferred','Expired'].map(s => <MenuItem key={s} value={s}>{s}</MenuItem>)}
              </Select>
            </FormControl>
            <FormControl fullWidth>
              <InputLabel>Condition at Discharge</InputLabel>
              <Select value={dischargeForm.dischargeType} label="Condition at Discharge" onChange={e => setDischargeForm(p => ({ ...p, dischargeType: e.target.value }))}>
                {['Cured','Improved','Same','Deteriorated','LAMA','Transferred','Expired'].map(s => <MenuItem key={s} value={s}>{s}</MenuItem>)}
              </Select>
            </FormControl>
            <TextField fullWidth label="Actual Discharge Date & Time" type="datetime-local" InputLabelProps={{ shrink: true }}
              value={dischargeForm.actualDischargeDate} onChange={e => setDischargeForm(p => ({ ...p, actualDischargeDate: e.target.value }))} />
            <TextField fullWidth label="Discharge Notes" multiline rows={3} value={dischargeForm.dischargeNotes}
              onChange={e => setDischargeForm(p => ({ ...p, dischargeNotes: e.target.value }))} />
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setDischargeDialog(false)}>Cancel</Button>
            <Button type="submit" variant="contained" color="success" disabled={discharging}>{discharging ? 'Processing…' : 'Confirm Discharge'}</Button>
          </DialogActions>
        </form>
      </Dialog>

      {/* ── Transfer Bed Dialog ── */}
      <Dialog open={transferDialog} onClose={() => setTransferDialog(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Transfer Bed — {patient ? `${patient.firstName} ${patient.lastName}` : ''}</DialogTitle>
        <form onSubmit={handleTransferBed}>
          <DialogContent sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 1 }}>
            <Typography variant="body2" color="text.secondary">
              Currently in <strong>{admission?.bed?.bedNumber}</strong> · {admission?.ward?.name}
            </Typography>
            <FormControl fullWidth required>
              <InputLabel>Target Ward</InputLabel>
              <Select value={transferForm.wardId} label="Target Ward *" onChange={e => handleWardChangeForTransfer(e.target.value)}>
                {transferWards.map(w => <MenuItem key={w.id} value={w.id}>{w.name}</MenuItem>)}
              </Select>
            </FormControl>
            <FormControl fullWidth required>
              <InputLabel>Target Bed</InputLabel>
              <Select value={transferForm.bedId} label="Target Bed *" disabled={!transferForm.wardId}
                onChange={e => setTransferForm(p => ({ ...p, bedId: e.target.value }))}>
                {transferBeds.length === 0 && <MenuItem value="" disabled>No available beds in this ward</MenuItem>}
                {transferBeds.map(b => <MenuItem key={b.id} value={b.id}>{b.bedNumber} ({b.bedType}) — {b.status}</MenuItem>)}
              </Select>
            </FormControl>
            <TextField fullWidth label="Reason for Transfer" multiline rows={2} value={transferForm.reason}
              onChange={e => setTransferForm(p => ({ ...p, reason: e.target.value }))} />

            {transferHistory.length > 0 && (
              <Box>
                <Divider sx={{ mb: 1.5 }} />
                <Typography variant="subtitle2" sx={{ mb: 1 }}>Transfer History</Typography>
                {transferHistory.map(t => (
                  <Box key={t.id} sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.75 }}>
                    <Typography variant="caption" color="text.secondary">
                      {t.fromBed} ({t.fromWard}) → {t.toBed} ({t.toWard}){t.reason ? ` — ${t.reason}` : ''}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">{fmtDT(t.transferredAt)}</Typography>
                  </Box>
                ))}
              </Box>
            )}
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setTransferDialog(false)}>Cancel</Button>
            <Button type="submit" variant="contained" disabled={transferring || !transferForm.bedId}>
              {transferring ? 'Transferring…' : 'Confirm Transfer'}
            </Button>
          </DialogActions>
        </form>
      </Dialog>

      {/* ── Add Vitals Dialog ── */}
      <Dialog open={vitalsDialog} onClose={() => setVitalsDialog(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Record Vitals</DialogTitle>
        <form onSubmit={handleSaveVitals}>
          <DialogContent>
            <Grid container spacing={2}>
              <Grid item xs={12}>
                <TextField fullWidth label="Recorded At" type="datetime-local" InputLabelProps={{ shrink: true }}
                  value={vitalsForm.recordedAt} onChange={e => setVitalsForm(p => ({ ...p, recordedAt: e.target.value }))} />
              </Grid>
              {[
                { key: 'temperature', label: 'Temperature (°C)', hint: '36.1–37.2' },
                { key: 'bpSystolic', label: 'BP Systolic (mmHg)', hint: '90–140' },
                { key: 'bpDiastolic', label: 'BP Diastolic (mmHg)', hint: '60–90' },
                { key: 'pulse', label: 'Pulse (bpm)', hint: '60–100' },
                { key: 'respiratoryRate', label: 'Respiratory Rate (/min)', hint: '12–20' },
                { key: 'spO2', label: 'SpO₂ (%)', hint: '≥95' },
                { key: 'weight', label: 'Weight (kg)', hint: '' },
                { key: 'bloodSugar', label: 'Blood Sugar (mg/dL)', hint: '70–140' },
              ].map(f => (
                <Grid item xs={6} key={f.key}>
                  <TextField fullWidth size="small" label={f.label} type="number" inputProps={{ step: 'any', min: 0 }}
                    value={vitalsForm[f.key]}
                    helperText={f.hint ? `Normal: ${f.hint}` : ''}
                    onChange={e => setVitalsForm(p => ({ ...p, [f.key]: e.target.value }))} />
                </Grid>
              ))}
              <Grid item xs={12}>
                <TextField fullWidth size="small" label="Notes" value={vitalsForm.notes} onChange={e => setVitalsForm(p => ({ ...p, notes: e.target.value }))} />
              </Grid>
            </Grid>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setVitalsDialog(false)}>Cancel</Button>
            <Button type="submit" variant="contained" disabled={savingVitals}>{savingVitals ? 'Saving…' : 'Save Vitals'}</Button>
          </DialogActions>
        </form>
      </Dialog>

      {/* ── Add / Edit Note Dialog ── */}
      <Dialog open={noteDialog.open} onClose={() => setNoteDialog({ open: false, editing: null })} maxWidth="md" fullWidth>
        <DialogTitle>{noteDialog.editing ? 'Edit Note' : 'Add Clinical Note'}</DialogTitle>
        <form onSubmit={handleSaveNote}>
          <DialogContent sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 1 }}>
            <Box sx={{ display: 'flex', gap: 2, alignItems: 'center', flexWrap: 'wrap' }}>
              <FormControl sx={{ minWidth: 160 }}>
                <InputLabel>Note Type</InputLabel>
                <Select size="small" value={noteForm.noteType} label="Note Type" onChange={e => setNoteForm(p => ({ ...p, noteType: e.target.value }))}>
                  {['Doctor','Nurse','Consultant'].map(t => <MenuItem key={t} value={t}>{t}</MenuItem>)}
                </Select>
              </FormControl>
              <TextField size="small" label="Title (optional)" sx={{ flex: 1 }} value={noteForm.title} onChange={e => setNoteForm(p => ({ ...p, title: e.target.value }))} />
              <ToggleButtonGroup size="small" exclusive value={noteMode} onChange={(_, v) => v && setNoteMode(v)}>
                <ToggleButton value="soap">SOAP</ToggleButton>
                <ToggleButton value="free">Free Text</ToggleButton>
              </ToggleButtonGroup>
            </Box>
            {noteMode === 'soap' ? (
              <Grid container spacing={1.5}>
                {[['subjective','S — Subjective (Patient complaints & history)'],['objective','O — Objective (Examination findings)'],['assessment','A — Assessment (Clinical impression)'],['plan','P — Plan (Treatment plan)']].map(([k, label]) => (
                  <Grid item xs={12} sm={6} key={k}>
                    <TextField fullWidth multiline rows={4} label={label} value={noteForm[k]} onChange={e => setNoteForm(p => ({ ...p, [k]: e.target.value }))} />
                  </Grid>
                ))}
              </Grid>
            ) : (
              <TextField fullWidth multiline rows={10} label="Notes" value={noteForm.freeText} onChange={e => setNoteForm(p => ({ ...p, freeText: e.target.value }))} />
            )}
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setNoteDialog({ open: false, editing: null })}>Cancel</Button>
            <Button type="submit" variant="contained" disabled={savingNote}>{savingNote ? 'Saving…' : noteDialog.editing ? 'Update' : 'Add Note'}</Button>
          </DialogActions>
        </form>
      </Dialog>

      {/* ── Order Medication Dialog ── */}
      <Dialog open={medDialog} onClose={() => setMedDialog(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Order Medication</DialogTitle>
        <form onSubmit={handleSaveMed}>
          <DialogContent>
            <Grid container spacing={2}>
              <Grid item xs={12}>
                <TextField required fullWidth label="Medicine Name" value={medForm.medicineName} onChange={e => setMedForm(p => ({ ...p, medicineName: e.target.value }))} />
              </Grid>
              <Grid item xs={6}>
                <TextField fullWidth label="Dose" placeholder="e.g. 500mg, 10mg/kg" value={medForm.dose} onChange={e => setMedForm(p => ({ ...p, dose: e.target.value }))} />
              </Grid>
              <Grid item xs={6}>
                <FormControl fullWidth>
                  <InputLabel>Route</InputLabel>
                  <Select value={medForm.route} label="Route" onChange={e => setMedForm(p => ({ ...p, route: e.target.value }))}>
                    {ROUTES.map(r => <MenuItem key={r} value={r}>{r}</MenuItem>)}
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={6}>
                <FormControl fullWidth>
                  <InputLabel>Frequency</InputLabel>
                  <Select value={medForm.frequency} label="Frequency" onChange={e => setMedForm(p => ({ ...p, frequency: e.target.value }))}>
                    {FREQUENCIES.map(f => <MenuItem key={f} value={f}>{f}</MenuItem>)}
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={6}>
                <TextField fullWidth label="Start Date" type="datetime-local" InputLabelProps={{ shrink: true }} value={medForm.startDate} onChange={e => setMedForm(p => ({ ...p, startDate: e.target.value }))} />
              </Grid>
              <Grid item xs={12}>
                <TextField fullWidth label="Instructions" value={medForm.instructions} placeholder="e.g. Take after meals, Dilute in 100ml NS" onChange={e => setMedForm(p => ({ ...p, instructions: e.target.value }))} />
              </Grid>
            </Grid>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setMedDialog(false)}>Cancel</Button>
            <Button type="submit" variant="contained" disabled={savingMed}>{savingMed ? 'Saving…' : 'Order'}</Button>
          </DialogActions>
        </form>
      </Dialog>

      {/* ── Record Administration Dialog ── */}
      <Dialog open={marDialog.open} onClose={() => setMarDialog({ open: false, orderId: null, medicineName: '' })} maxWidth="xs" fullWidth>
        <DialogTitle>Record Administration — {marDialog.medicineName}</DialogTitle>
        <form onSubmit={handleSaveMar}>
          <DialogContent sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 1 }}>
            <TextField fullWidth label="Scheduled For" type="datetime-local" InputLabelProps={{ shrink: true }}
              value={marForm.scheduledFor} onChange={e => setMarForm(p => ({ ...p, scheduledFor: e.target.value }))} />
            <FormControl fullWidth>
              <InputLabel>Status</InputLabel>
              <Select value={marForm.status} label="Status" onChange={e => setMarForm(p => ({ ...p, status: e.target.value }))}>
                {ADM_STATUSES.map(s => <MenuItem key={s} value={s}>{s}</MenuItem>)}
              </Select>
            </FormControl>
            {marForm.status === 'Given' && (
              <TextField fullWidth label="Administered At" type="datetime-local" InputLabelProps={{ shrink: true }}
                value={marForm.administeredAt} onChange={e => setMarForm(p => ({ ...p, administeredAt: e.target.value }))} />
            )}
            <TextField fullWidth label="Notes (optional)" value={marForm.notes} onChange={e => setMarForm(p => ({ ...p, notes: e.target.value }))} />
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setMarDialog({ open: false, orderId: null, medicineName: '' })}>Cancel</Button>
            <Button type="submit" variant="contained" disabled={savingMar}>{savingMar ? 'Saving…' : 'Record'}</Button>
          </DialogActions>
        </form>
      </Dialog>

      {/* ── Order Investigation Dialog ── */}
      <Dialog open={invDialog.open} onClose={() => setInvDialog({ open: false })} maxWidth="sm" fullWidth>
        <DialogTitle>Order Investigation</DialogTitle>
        <form onSubmit={async e => {
          e.preventDefault();
          setSavingInv(true);
          try {
            await ipdInvestigationService.order(admissionId, invForm);
            setInvDialog({ open: false });
            loadInvestigations();
          } catch { alert('Failed to order investigation'); }
          finally { setSavingInv(false); }
        }}>
          <DialogContent sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 1 }}>
            <TextField fullWidth required label="Investigation Name" placeholder="e.g. CBC, Urine R/M, Chest X-Ray"
              value={invForm.investigationName} onChange={e => setInvForm(p => ({ ...p, investigationName: e.target.value }))} />
            <FormControl fullWidth>
              <InputLabel>Category</InputLabel>
              <Select value={invForm.category} label="Category" onChange={e => setInvForm(p => ({ ...p, category: e.target.value }))}>
                {['Lab','Radiology','Microbiology','Pathology','Other'].map(c => <MenuItem key={c} value={c}>{c}</MenuItem>)}
              </Select>
            </FormControl>
            <TextField fullWidth label="Ordered At" type="datetime-local" InputLabelProps={{ shrink: true }}
              value={invForm.orderedAt} onChange={e => setInvForm(p => ({ ...p, orderedAt: e.target.value }))} />
            <TextField fullWidth label="Notes (optional)" value={invForm.notes} onChange={e => setInvForm(p => ({ ...p, notes: e.target.value }))} />
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setInvDialog({ open: false })}>Cancel</Button>
            <Button type="submit" variant="contained" disabled={savingInv}>{savingInv ? 'Saving…' : 'Order'}</Button>
          </DialogActions>
        </form>
      </Dialog>

      {/* ── Enter Result Dialog ── */}
      <Dialog open={resultDialog.open} onClose={() => setResultDialog({ open: false, id: null, name: '' })} maxWidth="sm" fullWidth>
        <DialogTitle>Enter Result — {resultDialog.name}</DialogTitle>
        <form onSubmit={async e => {
          e.preventDefault();
          setSavingResult(true);
          try {
            await ipdInvestigationService.enterResult(admissionId, resultDialog.id, resultForm);
            setResultDialog({ open: false, id: null, name: '' });
            loadInvestigations();
          } catch { alert('Failed to save result'); }
          finally { setSavingResult(false); }
        }}>
          <DialogContent sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 1 }}>
            <Box sx={{ display: 'flex', gap: 2 }}>
              <TextField fullWidth required label="Result Value" placeholder="e.g. 12.5, Negative, Moderate"
                value={resultForm.resultValue} onChange={e => setResultForm(p => ({ ...p, resultValue: e.target.value }))} />
              <TextField label="Unit" placeholder="g/dL" sx={{ width: 110 }}
                value={resultForm.unit} onChange={e => setResultForm(p => ({ ...p, unit: e.target.value }))} />
            </Box>
            <TextField fullWidth label="Reference Range" placeholder="e.g. 12–16 g/dL"
              value={resultForm.referenceRange} onChange={e => setResultForm(p => ({ ...p, referenceRange: e.target.value }))} />
            <FormControl fullWidth>
              <InputLabel>Flag</InputLabel>
              <Select value={resultForm.flag} label="Flag" onChange={e => setResultForm(p => ({ ...p, flag: e.target.value }))}>
                {['Normal','High','Low','Critical'].map(f => <MenuItem key={f} value={f}>{f}</MenuItem>)}
              </Select>
            </FormControl>
            <TextField fullWidth label="Result At" type="datetime-local" InputLabelProps={{ shrink: true }}
              value={resultForm.resultAt} onChange={e => setResultForm(p => ({ ...p, resultAt: e.target.value }))} />
            <TextField fullWidth label="Notes (optional)"
              value={resultForm.notes} onChange={e => setResultForm(p => ({ ...p, notes: e.target.value }))} />
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setResultDialog({ open: false, id: null, name: '' })}>Cancel</Button>
            <Button type="submit" variant="contained" disabled={savingResult}>{savingResult ? 'Saving…' : 'Save Result'}</Button>
          </DialogActions>
        </form>
      </Dialog>

      {/* ── Add Fluid Entry Dialog ── */}
      <Dialog open={fluidDialog} onClose={() => setFluidDialog(false)} maxWidth="xs" fullWidth>
        <DialogTitle>Add Fluid Entry</DialogTitle>
        <form onSubmit={async e => {
          e.preventDefault();
          setSavingFluid(true);
          try {
            await ipdFluidBalanceService.add(admissionId, { ...fluidForm, amountMl: parseFloat(fluidForm.amountMl) });
            setFluidDialog(false);
            loadFluid();
          } catch { alert('Failed to save fluid entry'); }
          finally { setSavingFluid(false); }
        }}>
          <DialogContent sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 1 }}>
            <ToggleButtonGroup exclusive fullWidth size="small"
              value={fluidForm.entryType}
              onChange={(_, v) => {
                if (!v) return;
                const defaultCat = v === 'Intake' ? 'IV Fluid' : 'Urine';
                setFluidForm(p => ({ ...p, entryType: v, category: defaultCat }));
              }}>
              <ToggleButton value="Intake" sx={{ color: '#1565c0', '&.Mui-selected': { bgcolor: '#e3f2fd', color: '#1565c0' } }}>
                Intake
              </ToggleButton>
              <ToggleButton value="Output" sx={{ color: '#c62828', '&.Mui-selected': { bgcolor: '#ffebee', color: '#c62828' } }}>
                Output
              </ToggleButton>
            </ToggleButtonGroup>
            <FormControl fullWidth>
              <InputLabel>Category</InputLabel>
              <Select value={fluidForm.category} label="Category"
                onChange={e => setFluidForm(p => ({ ...p, category: e.target.value }))}>
                {fluidForm.entryType === 'Intake'
                  ? ['IV Fluid','Oral','Blood Product','Other'].map(c => <MenuItem key={c} value={c}>{c}</MenuItem>)
                  : ['Urine','Drain','Vomit','Nasogastric','Stool','Other'].map(c => <MenuItem key={c} value={c}>{c}</MenuItem>)
                }
              </Select>
            </FormControl>
            <TextField fullWidth required label="Amount (mL)" type="number"
              inputProps={{ min: 0, step: 0.5 }}
              value={fluidForm.amountMl} onChange={e => setFluidForm(p => ({ ...p, amountMl: e.target.value }))} />
            <TextField fullWidth label="Description (optional)" placeholder="e.g. NS 500mL over 4h"
              value={fluidForm.description} onChange={e => setFluidForm(p => ({ ...p, description: e.target.value }))} />
            <TextField fullWidth label="Recorded At" type="datetime-local" InputLabelProps={{ shrink: true }}
              value={fluidForm.recordedAt} onChange={e => setFluidForm(p => ({ ...p, recordedAt: e.target.value }))} />
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setFluidDialog(false)}>Cancel</Button>
            <Button type="submit" variant="contained" disabled={savingFluid}>{savingFluid ? 'Saving…' : 'Add'}</Button>
          </DialogActions>
        </form>
      </Dialog>

      {/* ── Plan / Edit Surgery Dialog ── */}
      <SurgeryFormDialog
        open={surgeryDialog.open}
        editingItem={surgeryDialog.editing}
        fixedPatient={patient ? { id: patient.id, firstName: patient.firstName, lastName: patient.lastName, phoneNumber: patient.phoneNumber } : null}
        onClose={() => setSurgeryDialog({ open: false, editing: null })}
        onSaved={() => { setSurgeryDialog({ open: false, editing: null }); loadSurgeries(); }}
      />

      {/* ── Create Insurance Case Dialog ── */}
      <Dialog open={insuranceCreateDialog} onClose={() => setInsuranceCreateDialog(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Create Insurance Case</DialogTitle>
        <form onSubmit={handleCreateInsuranceCase}>
          <DialogContent>
            <Grid container spacing={2} sx={{ mt: 0.5 }}>
              <Grid item xs={12}>
                <FormControl fullWidth>
                  <InputLabel>Claim Type</InputLabel>
                  <Select
                    label="Claim Type"
                    value={insuranceCreateForm.claimType}
                    onChange={(e) => setInsuranceCreateForm({ ...insuranceCreateForm, claimType: e.target.value })}
                  >
                    <MenuItem value="Cashless">Cashless</MenuItem>
                    <MenuItem value="Reimbursement">Reimbursement</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} sm={6}>
                <Autocomplete
                  options={insuranceInsurers}
                  getOptionLabel={(o) => o.name || ''}
                  value={insuranceCreateForm.insurerId}
                  onChange={(_, val) => setInsuranceCreateForm({ ...insuranceCreateForm, insurerId: val })}
                  renderInput={(params) => <TextField {...params} label="Insurer" />}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <Autocomplete
                  options={insuranceTpas}
                  getOptionLabel={(o) => o.name || ''}
                  value={insuranceCreateForm.tpaId}
                  onChange={(_, val) => setInsuranceCreateForm({ ...insuranceCreateForm, tpaId: val })}
                  renderInput={(params) => <TextField {...params} label="TPA" />}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth label="Policy Number"
                  value={insuranceCreateForm.policyNumber}
                  onChange={(e) => setInsuranceCreateForm({ ...insuranceCreateForm, policyNumber: e.target.value })}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth label="Member ID"
                  value={insuranceCreateForm.memberId}
                  onChange={(e) => setInsuranceCreateForm({ ...insuranceCreateForm, memberId: e.target.value })}
                />
              </Grid>
            </Grid>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setInsuranceCreateDialog(false)}>Cancel</Button>
            <Button type="submit" variant="contained" disabled={savingInsurance}>Create</Button>
          </DialogActions>
        </form>
      </Dialog>
    </div>
  );
};

export default IpdPatientRecord;
